import React from 'react';
import { Link } from 'react-router-dom';
import { IconFacebookF, IconLinkedinIn, IconTwitter, IconYoutube, IconPhoneAlt, IconEnvelope, IconMapMarkerAlt, IconWhatsapp } from '../ui/icons';
import emblem from '../../assets/images/logo-emblem.png';
import { SITE } from '../../config/site';

/**
 * Pied de page commun à toutes les pages.
 * Coordonnées et identité : src/config/site.js (une seule source).
 */

const COLUMNS = [
  {
    title: 'Investir',
    links: [
      { path: '/projects', name: 'Tous les projets' },
      { path: '/investissement-revenus', name: 'Comment ça marche' },
      { path: '/epargne-positive', name: 'Épargne positive' },
      { path: '/diaspora', name: "Investir depuis l'étranger" },
      { path: '/etudes-de-cas', name: 'Études de cas' },
      { path: '/support-investisseurs', name: 'Aide aux investisseurs' }
    ]
  },
  {
    title: 'Entreprendre',
    links: [
      { path: '/financement', name: 'Faire financer mon projet' },
      { path: '/eligibilite', name: "Test d'éligibilité" },
      { path: '/incubateur', name: 'Incubateur du Centre Hub' },
      { path: '/criteres-selection', name: 'Critères de sélection' },
      { path: '/ressources', name: 'Ressources' },
      { path: '/devenir-mentor', name: 'Devenir mentor' },
      { path: '/faire-un-don', name: 'Faire un don' },
      { path: '/support-entrepreneurs', name: 'Aide aux entrepreneurs' }
    ]
  },
  {
    title: 'Nos engagements',
    links: [
      { path: '/transparence', name: 'Transparence' },
      { path: '/impact-social', name: 'Impact social' },
      { path: '/ecologie', name: 'Écologie' },
      { path: '/inclusion', name: 'Inclusion' },
      { path: '/securite', name: 'Sécurité' },
      { path: '/stats', name: 'Statistiques' },
      { path: '/rapports', name: 'Rapports' }
    ]
  },
  {
    title: 'À propos',
    links: [
      { path: '/about', name: 'Qui sommes-nous' },
      { path: '/pour-qui', name: 'Pour qui ?' },
      { path: '/agenda', name: 'Agenda' },
      { path: '/equipe', name: 'Équipe' },
      { path: '/partenaires', name: 'Partenaires' },
      { path: '/presse', name: 'Espace presse' },
      { path: '/recrutement', name: 'Recrutement' },
      { path: '/blog', name: 'Actualités' },
      { path: '/newsletter', name: 'Newsletter' }
    ]
  }
];

const LEGAL_LINKS = [
  { path: '/mentions-legales', name: 'Mentions légales' },
  { path: '/cgu', name: 'Conditions d’utilisation' },
  { path: '/confidentialite', name: 'Confidentialité' },
  { path: '/cookies', name: 'Cookies' },
  { path: '/reclamations', name: 'Réclamations' }
];

const SOCIAL = [
  { key: 'facebook', name: 'Facebook', icon: IconFacebookF },
  { key: 'linkedin', name: 'LinkedIn', icon: IconLinkedinIn },
  { key: 'twitter', name: 'X (Twitter)', icon: IconTwitter },
  { key: 'youtube', name: 'YouTube', icon: IconYoutube }
].filter(network => SITE.social[network.key]);

const linkClass = 'text-blue-100 hover:text-white hover:underline';

const Footer = () => (
  <footer className="bg-blue-900 text-blue-100">
    <div className="mx-auto grid max-w-6xl gap-10 px-4 py-14 sm:px-6 lg:grid-cols-[1.4fr_repeat(4,1fr)] lg:px-8">
      {/* Identité et coordonnées */}
      <div>
        <Link to="/" className="inline-flex items-center gap-3" aria-label={`${SITE.name} : accueil`}>
          <img src={emblem} alt="" width="44" height="44" className="h-11 w-11 rounded-lg bg-white p-1" />
          <span className="text-lg font-bold leading-tight text-white">
            Community<br /><span className="text-orange-300">ImpactInvest</span>
          </span>
        </Link>
        <p className="mt-4 text-sm leading-relaxed">{SITE.tagline}.</p>
        <ul className="mt-5 space-y-2 text-sm">
          <li className="flex items-center gap-2"><IconMapMarkerAlt aria-hidden="true" /> {SITE.address}</li>
          <li><a href={SITE.phoneHref} className={`inline-flex items-center gap-2 ${linkClass}`}><IconPhoneAlt aria-hidden="true" /> {SITE.phone}</a></li>
          <li><a href={SITE.whatsappHref} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 ${linkClass}`}><IconWhatsapp aria-hidden="true" /> WhatsApp</a></li>
          <li><a href={`mailto:${SITE.email}`} className={`inline-flex items-center gap-2 break-all ${linkClass}`}><IconEnvelope aria-hidden="true" /> {SITE.email}</a></li>
        </ul>
        {SOCIAL.length > 0 && (
          <ul className="mt-5 flex gap-3" aria-label="Réseaux sociaux">
            {SOCIAL.map(({ key, name, icon: Icon }) => (
              <li key={key}>
                <a href={SITE.social[key]} target="_blank" rel="noopener noreferrer" aria-label={name}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20">
                  <Icon aria-hidden="true" />
                </a>
              </li>
            ))}
          </ul>
        )}
      </div>

      {COLUMNS.map(column => (
        <nav key={column.title} aria-label={column.title}>
          <h2 className="text-base font-semibold text-white">{column.title}</h2>
          <ul className="mt-4 space-y-2 text-sm">
            {column.links.map(link => (
              <li key={link.path}><Link to={link.path} className={linkClass}>{link.name}</Link></li>
            ))}
          </ul>
        </nav>
      ))}
    </div>

    {/* Informations légales */}
    <div className="border-t border-blue-800">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-4 py-6 text-xs text-blue-200 sm:px-6 lg:flex-row lg:items-start lg:justify-between lg:px-8">
        <div className="space-y-1">
          <p><span className="font-semibold text-white">{SITE.legalName}</span> — {SITE.legalForm}</p>
          <p>Registre du commerce et du crédit mobilier de {SITE.city} : {SITE.rccm} (du {SITE.rccmDate}) · NIF : {SITE.nif}</p>
          <p>© {new Date().getFullYear()} {SITE.name}. Tous droits réservés.</p>
        </div>
        <ul className="flex flex-wrap gap-x-4 gap-y-2">
          {LEGAL_LINKS.map(link => (
            <li key={link.path}><Link to={link.path} className={linkClass}>{link.name}</Link></li>
          ))}
        </ul>
      </div>
    </div>
  </footer>
);

export default Footer;
